import React, { useState } from 'react';
import Section from './Section';
import StarRating from './StarRating';
import { useLocalTestimonials } from '../hooks/useLocalTestimonials';

const avatarColors = [
  'bg-primary text-white',
  'bg-violet-400 text-white',
  'bg-sky-400 text-white',
  'bg-emerald-400 text-white',
  'bg-amber-400 text-white',
  'bg-rose-400 text-white',
];

const QuoteIcon = () => (
  <svg className="w-8 h-8 text-primary/20" fill="currentColor" viewBox="0 0 24 24">
    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
  </svg>
);

const Testimonials = () => {
  const { testimonials, addTestimonial, averageRating } = useLocalTestimonials();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', childName: '', rating: 5, comment: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name && formData.childName && formData.comment) {
      addTestimonial(formData);
      setFormData({ name: '', childName: '', rating: 5, comment: '' });
      setShowForm(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getInitials = (name) =>
    name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();

  return (
    <Section id="testimonios" className="bg-gradient-to-b from-white to-[#fdf6ff]">

      {/* Header */}
      <div className="text-center mb-16">
        <span className="badge bg-secondary/40 text-text mb-4">Familias felices</span>
        <h2 className="section-title mb-4">Lo que dicen las familias</h2>
        <p className="section-subtitle mb-8">
          Experiencias reales de familias que han confiado en nosotros
        </p>

        {/* Average rating */}
        <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-6 py-4 shadow-md border border-gray-100">
          <p className="font-fredoka text-5xl text-primary leading-none">{averageRating}</p>
          <div className="text-left">
            <StarRating rating={Math.round(averageRating)} size="text-xl" />
            <p className="text-sm text-text/55 font-nunito mt-1">{testimonials.length} testimonios</p>
          </div>
        </div>
      </div>

      {/* Testimonial cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {testimonials.slice(0, 6).map((testimonial, index) => (
          <div
            key={testimonial.id}
            className="bg-white rounded-3xl p-6 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col border border-gray-50"
          >
            {/* Quote + stars row */}
            <div className="flex items-start justify-between mb-4">
              <QuoteIcon />
              <StarRating rating={testimonial.rating} size="text-sm" />
            </div>

            {/* Comment */}
            <p className="text-text/75 italic leading-relaxed flex-1 font-nunito text-sm mb-5">
              "{testimonial.comment}"
            </p>

            {/* Author */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-fredoka text-sm font-semibold flex-shrink-0 ${avatarColors[index % avatarColors.length]}`}>
                {getInitials(testimonial.name)}
              </div>
              <div>
                <p className="font-semibold text-text text-sm leading-none">{testimonial.name}</p>
                <p className="text-primary text-xs font-nunito mt-0.5">{testimonial.childName}</p>
              </div>
              <span className="ml-auto text-xs text-text/40 font-nunito">
                {new Date(testimonial.date).toLocaleDateString('es-CL', { month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Add testimonial button */}
      <div className="text-center">
        <button onClick={() => setShowForm(!showForm)} className="btn-secondary">
          {showForm ? 'Cancelar' : 'Agregar mi testimonio'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mt-8 max-w-2xl mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
            <h3 className="font-fredoka text-2xl text-text mb-6 text-center">
              Comparte tu experiencia
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-text mb-1.5">
                    Tu nombre *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-nunito text-sm"
                    placeholder="María González"
                  />
                </div>
                <div>
                  <label htmlFor="childName" className="block text-sm font-semibold text-text mb-1.5">
                    Nombre de tu hijo/a *
                  </label>
                  <input
                    type="text"
                    id="childName"
                    name="childName"
                    value={formData.childName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-nunito text-sm"
                    placeholder="Sofía, 6 años"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text mb-1.5">Calificación *</label>
                <StarRating
                  rating={formData.rating}
                  interactive={true}
                  onRatingChange={(rating) => setFormData((prev) => ({ ...prev, rating }))}
                  size="text-2xl"
                />
              </div>

              <div>
                <label htmlFor="comment" className="block text-sm font-semibold text-text mb-1.5">
                  Tu testimonio *
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none font-nunito text-sm"
                  placeholder="Comparte tu experiencia con el Centro Volantín..."
                />
              </div>

              <div className="flex justify-center gap-4 pt-2">
                <button type="submit" className="btn-primary">
                  Enviar testimonio
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Section>
  );
};

export default Testimonials;
